import {useState} from 'react';
import Model from './Model';
import API from '../Services/API';
import State from './State';
import { useToasts } from '../Components/Toasts';

const useAsk = (alwaysRefresh, question, recipient) => {
    const ret = Model({
        stateKey: ['help', 'ask', question],
        alwaysRefresh: alwaysRefresh,
        block: !question,
        fetchData: () => API.get(`/help/ask?question=${question}&socketRecipient=${recipient}`),
        setAdditionalData: (data) => {
            data.docs.forEach(doc => {
                State.putIfNotExists(['help', 'doc', doc.id], doc)
            })
        },
        returnDataKey: 'data',
        defaultLoadingError: 'Sorry, there was an error loading help.',
    })
    return {
        answer: ret?.data?.answer ?? null,
        docs: ret?.data?.docs ?? [],
        activityId: ret?.data?.activityId ?? null,
        ...ret,
    }
}

const useDoc = (id) => {
    const ret = Model({
        block: !id,
        stateKey: ['help', 'doc', id],
        fetchData: () => API.get('/help/view/' + id),
        returnDataKey: 'data',
        defaultLoadingError: 'Sorry, there was an error loading help.',
    })
    return {
        doc: ret?.data?.doc,
        activityId: ret?.data?.activityId,
        ...ret,
    }
}

const useRecommendPrompt = (showing) => {
    const ret = Model({
        block: showing === false,
        stateKey: ['help', 'recommendPrompt'],
        fetchData: () => API.get('/help/recommendPrompt'),
        returnDataKey: 'data',
        defaultLoadingError: null,
    })
    return {
        activityId: ret?.data?.activityId,
        ...ret,
    }
}

const useFeedback = (activityId) => {
    const [feedback, setFeedback] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const {showError} = useToasts();

    const giveFeedback = async (feedback) => {
        setIsLoading(true);
        setFeedback(feedback);
        try {
            await API.post('/help/feedback', {activityId, feedback});
        } catch (err) {
            showError(API.parseErrorResponse(err));
        }
        setIsLoading(false);
    }

    return {
        feedback,
        isLoading,
        giveFeedback,
    }
}

const useSuggestion = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {showError, showSuccess} = useToasts();

    const submit = (suggestion) => {
        setIsSubmitting(true);

        return API.post('/help/suggestion', {suggestion})
            .then(() => {
                setIsSubmitting(false);
                showSuccess('Thanks for your feedback!');
            }).catch(err => {
                setIsSubmitting(false);
                showError(API.parseErrorResponse(err))
            })
    }

    return {
        isSubmitting,
        submit
    }
}

const useVideos = (alwaysRefresh) => {
    return Model({
        stateKey: ['help', 'videos'],
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/help/videos`),
        returnDataKey: 'videos',
        defaultLoadingError: 'Sorry, there was an error loading the videos.',
    })
}

export {
    useAsk,
    useDoc,
    useRecommendPrompt,
    useFeedback,
    useSuggestion,
    useVideos,
}
